/**
 * Error handling utilities for HTTP errors
 * Provides type-safe error checking and data extraction
 */

/**
 * Standard HTTP error structure from Axios
 */
export interface HttpError {
  response: {
    status: number
    data?: string | Record<string, unknown>
  }
}

/**
 * Type guard to check if an error is an HTTP error with response
 * @param err - The error to check
 * @returns True if the error has a response object with status
 */
export const isHttpError = (err: unknown): err is HttpError => {
  const error = err as { response?: { status?: number } }
  return typeof error?.response?.status === 'number'
}

/**
 * Type guard to check if an error is a client error (4xx)
 * @param err - The error to check
 * @returns True if the error status is between 400-499
 */
export const isClientError = (err: unknown): err is HttpError => {
  return (
    isHttpError(err)
    && err.response.status >= 400
    && err.response.status < 500
  )
}

/**
 * Type guard to check if an error is a server error (5xx)
 * @param err - The error to check
 * @returns True if the error status is 500 or above
 */
export const isServerError = (err: unknown): err is HttpError => {
  return isHttpError(err) && err.response.status >= 500
}

/**
 * Extract error status code from an error object
 * @param err - The error object
 * @returns The HTTP status code or undefined
 */
export const getErrorStatus = (err: unknown): number | undefined => {
  if (isHttpError(err)) {
    return err.response.status
  }
  // Fallback for errors with status at root level
  const error = err as { status?: number }
  return error?.status
}

/**
 * Extract error data/message from an error object
 * @param err - The error object
 * @returns The error data (string, object, or undefined)
 *
 * @example
 * // Handling string error messages
 * const errorData = getErrorData(error);
 * if (typeof errorData === 'string') {
 *   toast.error(errorData);
 * }
 *
 * @example
 * // Handling structured error objects
 * const errorData = getErrorData(error);
 * if (typeof errorData === 'object' && errorData !== null) {
 *   const { errorCode, message, details } = errorData;
 *   toast.error(message || 'An error occurred');
 * }
 *
 * @example
 * // Safe handling of all cases
 * const errorData = getErrorData(error);
 * if (!errorData) {
 *   // No error data available
 *   toast.error('An unexpected error occurred');
 * } else if (typeof errorData === 'string') {
 *   // String message
 *   toast.error(errorData);
 * } else {
 *   // Object - extract relevant fields
 *   toast.error(errorData.message as string || 'Error occurred');
 * }
 *
 * @example
 * // Using with type assertions (when structure is known)
 * const errorData = getErrorData(error) as { errorCode: string; message: string };
 * if (errorData?.errorCode === 'VALIDATION_ERROR') {
 *   handleValidationError(errorData.message);
 * }
 */
export const getErrorData = (
  err: unknown,
): string | Record<string, unknown> | undefined => {
  if (isHttpError(err)) {
    return err.response.data
  }
  // Fallback for errors with data at root level
  const error = err as { data?: string | Record<string, unknown> }
  return error?.data
}

export const getErrorMessage = (err: unknown): string | undefined => {
  const data = getErrorData(err)
  if (data === undefined) return undefined
  if (typeof data === 'string') return data
  const detail = data.detail ?? data.title ?? data.message
  return typeof detail === 'string' ? detail : undefined
}

/**
 * Check if error data contains specific error code
 * @param err - The error object
 * @param errorCode - The error code to check for
 * @returns True if the error data contains the error code
 */
export const hasErrorCode = (err: unknown, errorCode: string): boolean => {
  const data = getErrorData(err)
  if (typeof data === 'object' && data !== null) {
    return 'errorCode' in data && data.errorCode === errorCode
  }
  return false
}

/**
 * React Query retry helper - don't retry on client errors (4xx)
 * @param failureCount - Current failure count
 * @param err - The error that occurred
 * @param maxRetries - Maximum number of retries (default: 3)
 * @returns True if should retry, false otherwise
 */
export const shouldRetryRequest = (
  failureCount: number,
  err: unknown,
  maxRetries = 3,
): boolean => {
  // Don't retry client errors (4xx) - these won't resolve with retry
  if (isClientError(err)) {
    return false
  }
  // Retry server errors and network errors up to max retries
  return failureCount < maxRetries
}
